import React, { useState } from "react";
import styled from "styled-components";

import { gql } from "@examspark/api";
import { assertNativeError } from "@examspark/common";
import {
  Form,
  Modal,
  ModalProps,
  SubmitButtons,
  TextArea,
  TextInput,
  SelectInput,
  VSpace,
} from "@examspark/common-ui";
import { PaddedBody } from "@examspark/common-ui/dist/components/modals/Modal";
import { IconFeedback } from "@examspark/exam-ui";
import { AnyObject } from "@examspark/types";

import { AppState } from "../providers";

const InstructionsContainer = styled.div`
  & > p {
    margin: 0.25rem 0 1.25rem;
  }
  & > hr {
    border: none;
    border-top: 1px solid #979797;
    height: 0;
    margin: 2rem 0 1rem;
  }
`;

const IconPreview = styled.span`
  height: 1.5rem;
  width: 1.5rem;
  margin: 0 0.5rem;
  display: inline-block;
  vertical-align: middle;

  svg {
    /* width: 1.6rem; */
    display: block;
    fill: #333;
  }
`;

export type FeedbackModalProps = {
  resolvedAuthContext: {
    appId: string;
    workspaceId: string;
    userId: string;
  };
  showItemFeedbackHint?: boolean;
  userEmail: string;
  appContext: AppState<AnyObject, AnyObject>;
  options: string[];
} & ModalProps;

const FeedbackModal = ({
  resolvedAuthContext,
  ...props
}: FeedbackModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [subject, setSubject] = useState("");
  const [feedbackType, setFeedbackType] = useState<string>("Technical Issue");
  const [result, setResult] = useState<string | null>(null);

  const onSubmit = async () => {
    const userEmail = props.appContext.user?.email ?? "";
    const additionalData: Record<string, string> = {
      Name: `${props.appContext.user?.firstName ?? ""} ${props.appContext.user?.lastName ?? ""}`,
      Email: userEmail,
      Workspace: props.appContext.workspace?.name ?? "",
      "Workspace ID": props.appContext.workspace?.id ?? "",
      "Page Title": document.title,
      "Page URL": window.location.href,
      "Feedback Type": feedbackType,
      Browser: navigator.userAgent,
      OS: navigator.platform,
    };

    try {
      setIsSubmitting(true);
      const response = (
        await gql.operations.feedback.submitFeedback({
          appId: resolvedAuthContext.appId,
          subject,
          feedback,
          userEmail,
          additionalData: JSON.stringify(additionalData),
        })
      ).data?.submitFeedback;

      if (!response) {
        setResult("There was a problem submitting your feedback.");
      } else if (
        response.__typename === "SubmitFeedbackErrorResponse" &&
        response.error
      ) {
        setResult(
          `There was a problem submitting your feedback: ${response.error}`,
        );
      } else {
        setResult("Thank you for submitting your feedback!");
      }
      setIsSubmitting(false);
    } catch (error) {
      assertNativeError(error);
      alert(error.message);
      setIsSubmitting(false);
    }
  };

  const onAfterClose = () => {
    setIsSubmitting(false);
    if (result) {
      setResult(null);
      setFeedbackType("Technical Issue");
      setSubject("");
      setFeedback("");
    }
  };

  return (
    <Modal
      title="Feedback"
      size="small"
      onAfterClose={onAfterClose}
      buttons={
        <SubmitButtons
          onCancel={props.onRequestClose}
          isSubmitting={isSubmitting}
          isSubmitDisabled={!subject || !feedback || result != null}
          submitLabel={"Submit Feedback"}
          onSubmit={onSubmit}
        />
      }
      {...props}
    >
      <PaddedBody>
        <VSpace>
          {(result && <p>{result}</p>) || (
            <>
              <InstructionsContainer>
                <p>
                  This form is for <strong>feedback only</strong>. We read, but
                  do not respond to, every submission.
                  <br />
                  <strong>For support please contact your support team.</strong>
                  <br />
                  <br />
                  <em>Thank you for helping us improve!</em>
                </p>
                {props.showItemFeedbackHint && (
                  <p>
                    <strong>Note:</strong> for feedback on an assessment item
                    use the
                    <IconPreview>
                      <IconFeedback />
                    </IconPreview>
                    button in the item preview.
                  </p>
                )}
              </InstructionsContainer>
              <Form.Field label="Feedback Type">
                <SelectInput
                  value={feedbackType}
                  onChange={(e) => {
                    if (e) {
                      setFeedbackType(e.value);
                    }
                  }}
                  options={props.options.map((o) => {
                    return { value: o, label: o };
                  })}
                />
              </Form.Field>
              <Form.FieldList>
                <Form.Field label="Subject line">
                  <TextInput
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </Form.Field>
                <Form.Field label="Feedback">
                  <TextArea
                    value={feedback}
                    rows={8}
                    onChange={(e) => setFeedback(e.target.value)}
                  />
                </Form.Field>
              </Form.FieldList>
            </>
          )}
        </VSpace>
      </PaddedBody>
    </Modal>
  );
};

export default FeedbackModal;
